import type { AxiosRequestConfig } from "axios"

import { BannerResponse, ColumnsConfig, ColumnsConfigListResponse, ColumnsResponse } from "@/api/common/types/Banner"
import {
  CommonImagesApiResponse,
  CommonImagesBatchDeleteRequest,
  CommonImagesQuery,
  CommonImageRegisterRequest,
  CommonImageRegisterResponse,
  CommonImageShowApiResponse,
  CommonImagesSyncApiResponse,
  CommonImagesSyncRequest,
  CommonImageStagedUploadRequest,
  CommonImageStagedUploadResponse,
  CommonImageUploadApiResponse,
} from "@/api/common/types/CommonImages"
import { MonorailProduceRequest } from "@/api/common/types/Monorail"
import {
  APPInstallResponse,
  RecommendAppsHelloResponse,
  RecommendAppsResponse,
} from "@/api/common/types/RecommendApps"
import { ShopifyAppExtensionsResponse } from "@/api/common/types/ShopifyAppExtensions"
import { getSessionToken } from "@/api/common/utils"
import { COLUMNTYPE } from "@/enums/global"
import { PopupsEnum } from "@/models/IUserInfo"
import { IConvertWebVitalsData } from "@/reportWebVitals"
import baseRequest from "@/utils/baseRequest"
import { CancelTypesEnum } from "@/utils/request.d"

export default {
  getCommonImages(params: CommonImagesQuery) {
    return baseRequest.get<CommonImagesApiResponse>("v4/common/images", { params })
  },

  uploadCommonImage(formData: FormData, config?: AxiosRequestConfig) {
    const uploadConfig: AxiosRequestConfig = {
      ...config,
      headers: {
        Accept: "application/json",
        ...config?.headers,
      },
      timeout: config?.timeout ?? 40_000,
      // 由调用方在 UI（如 DropZone 旁）展示 413/415/422 等，避免与全局 toast 重复
      useDefaultExceptionHandler: config?.useDefaultExceptionHandler ?? false,
    }

    return baseRequest.post<CommonImageUploadApiResponse>(
      "v4/common/images/upload",
      formData,
      uploadConfig,
    )
  },

  getCommonImageShow(params: { gid: string }) {
    return baseRequest.get<CommonImageShowApiResponse>("v4/common/images/show", { params })
  },

  /** POST /v4/common/images/staged-upload — 获取临时上传链接 */
  getStagedUpload(body: CommonImageStagedUploadRequest) {
    return baseRequest.post<CommonImageStagedUploadResponse>(
      "v4/common/images/staged-upload",
      body,
      { useDefaultExceptionHandler: false } as never,
    )
  },

  /** POST /v4/common/images — 将已上传至 GCS 的文件注册为 Shopify MediaImage */
  registerCommonImage(body: CommonImageRegisterRequest) {
    return baseRequest.post<CommonImageRegisterResponse>(
      "v4/common/images",
      body,
      { useDefaultExceptionHandler: false } as never,
    )
  },

  /** DELETE /v4/common/images/{gid} — HTTP 200 即删除成功 */
  deleteCommonImage(gid: string) {
    return baseRequest.delete<void>(`v4/common/images/${encodeURIComponent(gid)}`)
  },

  /** DELETE /v4/common/images — 批量删除，body: `{ gids }` */
  batchDeleteCommonImages(gids: string[]) {
    if (gids.length === 0) {
      return Promise.resolve({ status: 200, data: undefined })
    }

    const body: CommonImagesBatchDeleteRequest = { gids }

    return baseRequest.delete<void>("v4/common/images", { data: body })
  },

  /**
   * POST /v4/common/images/sync — 同步给定 gids：
   * 返回仍存在的 `items` 与服务端已同步删除的 `deleted_gids`
   */
  syncCommonImages(gids: string[]) {
    const body: CommonImagesSyncRequest = { gids }

    return baseRequest.post<CommonImagesSyncApiResponse>("v4/common/images/sync", body)
  },

  getBanner(bannerId: number) {
    return baseRequest.get<BannerResponse>(`v4/common/banners/${bannerId}`)
  },
  closePopup(type: PopupsEnum) {
    return baseRequest.post<BannerResponse>(`v2/common/popups/${type}/close`)
  },

  // 重启启用指定 Popup
  reopenPopup(type: PopupsEnum) {
    return baseRequest.delete<BannerResponse>(`v2/common/popups/${type}/reopen`)
  },

  updateBanner(bannerId: number, visible: boolean) {
    return baseRequest.put<BannerResponse>(`v4/common/banners/${bannerId}`, { banner: { visible } })
  },
  // 获取统计的列
  getColumnConfig(type: COLUMNTYPE) {
    return baseRequest.get<ColumnsResponse>(`v2/common/columns_config/${type}`)
  },
  // 设置统计的列
  setColumnConfig(type: COLUMNTYPE, list:ColumnsConfig[]) {
    return baseRequest.put<ColumnsResponse>(`v2/common/columns_config/${type}`, {
      columns_config: list,
    })
  },

  // 批量获取统计的列配置
  getColumnConfigList(types: string[]) {
    return baseRequest.get<ColumnsConfigListResponse>("v2/common/columns_config", { params: { types } })
  },

  // 获取推荐应用 hello 列表
  getRecommendAppsHello() {
    return baseRequest.get<RecommendAppsHelloResponse>("v2/common/recommend_apps/hello")
  },

  // 获取推荐应用列表
  getRecommendApps() {
    return baseRequest.get<RecommendAppsResponse>("v2/common/recommend_apps")
  },

  // Monorail 埋点上报
  monorailProduce(data: MonorailProduceRequest) {
    return baseRequest.post("v2/common/monorail/produce", data, {
      // @ts-ignore
      useDefaultExceptionHandler: false,
      params: {
        _t: Date.now(), // 在URL参数中添加时间戳，确保每次调用都是唯一的
      },
    })
  },

  // 获取 Return 安装状态
  getReturnInstall() {
    return baseRequest.get<APPInstallResponse>("v2/integration/return/status", {
      data: { cancelType: CancelTypesEnum.PATH },
      params: { integration: "return" },
    })
  },

  // 获取 TrustWill 安装状态
  getTrustInstall() {
    return baseRequest.get<APPInstallResponse>("v2/integration/trustwill/status", {
      data: { cancelType: CancelTypesEnum.PATH },
      params: { integration: "trustwill" },
    })
  },

  getShopifyAppExtensions() {
    return baseRequest.get<ShopifyAppExtensionsResponse>("v2/common/shopify_app/extensions").then(({ data }) => data.shopify_app_extensions)
  },

  putShopifyAppExtensions(hash_id: string, shopify_app_extensions: Pick<ShopifyAppExtensionsResponse, "shopify_app_extensions">) {
    return baseRequest.put("v2/common/shopify_app/extensions", {
      hash_id,
      shopify_app_extensions,
    })
  },
}

/**
 * 上报数据
 * @param events
 */
export const sendWebVitals = (events: IConvertWebVitalsData[]) => {
  const { downlink = null, saveData = null, effectiveType = null, type = null, rtt = null } = (navigator as any).connection || {}
  const connection = { downlink, saveData, effectiveType, type, rtt }

  let url = `${baseRequest.defaults.baseURL}v3/common/client_metrics`

  const body = JSON.stringify({
    connection,
    events,
    local: window.navigator.language,
    pathname: window.location.pathname,
    timezone: `UTC${(0 - new Date().getTimezoneOffset() / 60)}`,
  })

  getSessionToken()
    .then((token: string) => {
      url = `${url}?_jwt_token=${token}`

      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, body)
      } else {
        fetch(url, { body, method: "POST", keepalive: true })
      }
    })
}
